import React, { createContext, useContext, useState, ReactNode } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import { Collections } from '../types/collection';
import { captureException } from '@sentry/react';

// Enhanced onboarding step type
export type OnboardingStep = 'welcome' | 'goals' | 'experience' | 'role-industry' | 'profile' | 'interview-preferences' | 'permissions' | 'complete';

// Enhanced onboarding data interface
export interface OnboardingData {
  step: OnboardingStep;
  goals: string[];
  experience: string;
  roleIndustry: {
    targetRole: string;
    industry: string;
    careerLevel: string;
  };
  profile: {
    currentRole: string;
    skills: string[];
    location: string;
    linkedinUrl?: string;
  };
  interviewPreferences: {
    interviewTypes: string[];
    difficulty: string;
    focusAreas: string[];
    feedbackStyle: string;
  };
  sessionPreferences: {
    sessionLength: string;
    frequency: string;
  };
  permissions: {
    microphone: boolean;
    camera: boolean;
  };
  isComplete: boolean;
}

// Enhanced context interface
interface OnboardingContextType {
  data: OnboardingData;
  setStep: (step: OnboardingStep) => void;
  setGoals: (goals: string[]) => void;
  setExperience: (experience: string) => void;
  setRoleIndustry: (roleIndustry: { targetRole: string; industry: string; careerLevel: string }) => void;
  setProfile: (profile: { currentRole: string; skills: string[]; location: string; linkedinUrl?: string }) => void;
  setInterviewPreferences: (preferences: { interviewTypes: string[]; difficulty: string; focusAreas: string[]; feedbackStyle: string }) => void;
  setSessionPreferences: (preferences: { sessionLength: string; frequency: string }) => void;
  setPermissions: (permissions: { microphone: boolean; camera: boolean }) => void;
  nextStep: () => void;
  previousStep: () => void;
  completeOnboarding: () => void;
  saveToFirestore: () => Promise<void>;
  getProgress: () => number;
}

// Enhanced default data
const defaultData: OnboardingData = {
  step: 'welcome',
  goals: [],
  experience: '',
  roleIndustry: {
    targetRole: '',
    industry: '',
    careerLevel: ''
  },
  profile: {
    currentRole: '',
    skills: [],
    location: '',
    linkedinUrl: ''
  },
  interviewPreferences: {
    interviewTypes: [],
    difficulty: '',
    focusAreas: [],
    feedbackStyle: ''
  },
  sessionPreferences: {
    sessionLength: '15min',
    frequency: 'weekly'
  },
  permissions: {
    microphone: false,
    camera: false
  },
  isComplete: false
};

// Create context
const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Enhanced step order
const STEPS: OnboardingStep[] = ['welcome', 'goals', 'experience', 'role-industry', 'profile', 'interview-preferences', 'permissions', 'complete'];

// Enhanced provider component
export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState<OnboardingData>(defaultData);

  const setStep = (step: OnboardingStep) => {
    setData(prev => ({ ...prev, step }));
  };

  const setGoals = (goals: string[]) => {
    setData(prev => ({ ...prev, goals }));
  };

  const setExperience = (experience: string) => {
    setData(prev => ({ ...prev, experience }));
  };

  const setRoleIndustry = (roleIndustry: { targetRole: string; industry: string; careerLevel: string }) => {
    setData(prev => ({ ...prev, roleIndustry }));
  };

  const setProfile = (profile: { currentRole: string; skills: string[]; location: string; linkedinUrl?: string }) => {
    setData(prev => ({ ...prev, profile }));
  };

  const setInterviewPreferences = (preferences: { interviewTypes: string[]; difficulty: string; focusAreas: string[]; feedbackStyle: string }) => {
    setData(prev => ({ ...prev, interviewPreferences: preferences }));
  };

  const setSessionPreferences = (preferences: { sessionLength: string; frequency: string }) => {
    setData(prev => ({ ...prev, sessionPreferences: preferences }));
  };

  const setPermissions = (permissions: { microphone: boolean; camera: boolean }) => {
    setData(prev => ({ ...prev, permissions }));
  };

  const nextStep = () => {
    const currentIndex = STEPS.indexOf(data.step);
    if (currentIndex < STEPS.length - 1) {
      setStep(STEPS[currentIndex + 1]);
    }
  };

  const previousStep = () => {
    const currentIndex = STEPS.indexOf(data.step);
    if (currentIndex > 0) {
      setStep(STEPS[currentIndex - 1]);
    }
  };

  const saveToFirestore = async () => {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      const onboardingProfile = {
        goals: data.goals,
        experience: data.experience,
        roleIndustry: data.roleIndustry,
        profile: data.profile,
        interviewPreferences: data.interviewPreferences,
        sessionPreferences: data.sessionPreferences,
        permissions: data.permissions,
        completedAt: new Date(),
        isComplete: true
      };

      // Save to user document
      await firebase.firestore()
        .collection(Collections.Users)
        .doc(user.uid)
        .update({
          onboardingProfile,
          onboardingCompleted: true,
          updatedAt: new Date()
        });
    } catch (error) {
      captureException(error);
      throw error;
    }
  };

  const completeOnboarding = async () => {
    try {
      await saveToFirestore();
      setData(prev => ({ ...prev, isComplete: true, step: 'complete' }));
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Still allow progression to completion step even if save fails
      setData(prev => ({ ...prev, isComplete: true, step: 'complete' }));
    }
  };

  const getProgress = () => {
    const currentIndex = STEPS.indexOf(data.step);
    return Math.round((currentIndex / (STEPS.length - 1)) * 100);
  };

  const value: OnboardingContextType = {
    data,
    setStep,
    setGoals,
    setExperience,
    setRoleIndustry,
    setProfile,
    setInterviewPreferences,
    setSessionPreferences,
    setPermissions,
    nextStep,
    previousStep,
    completeOnboarding,
    saveToFirestore,
    getProgress
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

// Hook to use onboarding context
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};
